import React from 'react';
import ReactDOM from 'react-dom';
import { ExternalLink, Image, FileText, Plus, Database, Zap, Clipboard, ChevronDown } from 'react-feather';
import { Avatar } from '../../Avatar';
import { CadeadoTravada, CORES_SITUACAO, SituacaoGlifo } from '../../SituacaoGlifo';
import { CellRenderer } from './gridTypes';
import { API_ENDPOINT } from './gridConstants';

const DropdownCell: React.FC<{value: any, handlers?: Record<string, (params: any) => void>}> = ({value, handlers = {}}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [position, setPosition] = React.useState({top: 0, left: 0});
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const dropdownWidth = 220;
            const dropdownHeight = 250;

            let left = rect.left + window.scrollX;
            if (left + dropdownWidth > window.innerWidth) {
                left = rect.right + window.scrollX - dropdownWidth;
            }

            let top = rect.bottom + window.scrollY + 2;
            if (rect.bottom + dropdownHeight > window.innerHeight) {
                top = rect.top + window.scrollY - dropdownHeight - 2;
            }

            setPosition({
                top: Math.max(10, top),
                left: Math.max(10, left)
            });
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            updatePosition();
        }
    }, [isOpen]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (!value) return null;

    try {
        const dropdownData = typeof value === 'string'
            ? JSON.parse(value)
            : value;

        const dropdownMenu = isOpen && (
            <div
                ref={dropdownRef}
                style={{
                    position: 'absolute',
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    zIndex: 9999,
                    minWidth: '220px',
                    maxWidth: '280px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    overflow: 'hidden'
                }}
            >
                {dropdownData.items?.map((item: any, index: number) => {
                    const iconMap: Record<string, any> = {
                        'Database': Database,
                        'FileText': FileText,
                        'Image': Image,
                        'Zap': Zap,
                        'Clipboard': Clipboard
                    };

                    const IconComponent = item.icon ? iconMap[item.icon] : null;

                    const baseStyle = {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        color: '#333',
                        textDecoration: 'none',
                        fontSize: '13px',
                        borderBottom: index < dropdownData.items.length - 1 ? '1px solid #eee' : 'none',
                        transition: 'background-color 0.15s ease'
                    };

                    const handleMouseEnter = (e: any) => {
                        e.currentTarget.style.backgroundColor = '#f0f7ff';
                        e.currentTarget.style.color = '#007bff';
                    };

                    const handleMouseLeave = (e: any) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#333';
                    };

                    if (item.handler && handlers[item.handler]) {
                        return (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsOpen(false);
                                    handlers[item.handler](item.params || {});
                                }}
                                style={{
                                    ...baseStyle,
                                    width: '100%',
                                    textAlign: 'left',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                {IconComponent && <IconComponent size={16} />}
                                {item.title}
                            </button>
                        );
                    }

                    const fullUrl = `${API_ENDPOINT}${item.url}`;
                    return (
                        <a
                            key={index}
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={baseStyle}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            {IconComponent && <IconComponent size={16} />}
                            {item.title}
                        </a>
                    );
                })}
            </div>
        );

        return (
            <div className="cell-action">
                <button
                    ref={buttonRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="btn-dropdown-cell"
                >
                    <span>{dropdownData.title || 'Menu'}</span>
                    <ChevronDown size={12} style={{ opacity: 0.6 }} />
                </button>

                {isOpen && ReactDOM.createPortal(dropdownMenu, document.body)}
            </div>
        );
    } catch (e) {
        console.error('Error parsing dropdown data:', e);
        return null;
    }
};

const corDaSituacao = (situacao: string, field: { renderParams?: Record<string, any> }): string | null => {
    if (!situacao || !(situacao in CORES_SITUACAO)) return null;
    const cor = (field.renderParams as any)?.colors?.[situacao];
    return typeof cor === 'string' && cor ? cor : CORES_SITUACAO[situacao];
};

export const createCellRenderers = (handlers: Record<string, (params: any) => void> = {}): Record<string, CellRenderer> => ({
    Link: ({ value }) => {
        if (!value) return null;
        const fullUrl = `${API_ENDPOINT}${value}`;
        return (
            <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="grid-cell-link">
                <ExternalLink size={14} />
            </a>
        );
    },
    Moeda: ({ value }) => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
    },
    Percent: ({ value }) => {
        if (value === null || value === undefined) return '';
        const numValue = Number(value);
        return <span className={numValue > 0 ? 'up' : 'down'}>{numValue.toFixed(2)}%</span>;
    },
    Image: ({ value }) => {
        if (!value) return null;
        return (
            <a href={value} target="_blank" rel="noopener noreferrer">
                <Image size={14} />
            </a>
        );
    },
    Tag: ({ value, field }) => {
        if (!value) return null;
        try {
            const valueList = typeof value === 'string' ? JSON.parse(value) : value;
            if (!Array.isArray(valueList)) return String(value);
            return (
                <span className="grid-cell-tags">
                    {valueList.map((item: any) => (
                        <span key={item} className="grid-cell-tag">
                            {field.filterList?.[item] || item}
                        </span>
                    ))}
                </span>
            );
        } catch {
            return String(value);
        }
    },
    Avatar: ({ value, row, field }) => {
        if (!value) return null;
        const name = field.filterList?.[value] ?? (typeof value === 'string' ? value : String(value));
        // `renderParams.marcador`: coluna booleana da linha que acende um ícone ao lado do nome.
        const marcador = (field.renderParams as any)?.marcador;
        const marcado = marcador && [true, 1, '1', 't'].includes(row?.[marcador]);
        if (!marcado) return <Avatar name={name} size="sm" showName />;
        return (
            <span className="grid-avatar-marcador">
                <Avatar name={name} size="sm" showName />
                <span title={(field.renderParams as any)?.marcadorTitulo}><FileText size={13} /></span>
            </span>
        );
    },
    StatusTag: ({ value, field }) => {
        if (value === null || value === undefined || value === '') return null;
        const label = field.filterList?.[value] ?? String(value);
        const tone = (field.renderParams as any)?.colors?.[value] ?? 'muted';
        // Hex vindo do cadastro em vez de nome de tom: as etapas do app de campo têm
        // paleta própria, que os tons fixos do grid não reproduzem.
        if (typeof tone === 'string' && tone.startsWith('#')) {
            return <span className="grid-tag" style={{ background: tone }}>{label}</span>;
        }
        return <span className={`grid-tag grid-tag-${tone}`}>{label}</span>;
    },
    SituacaoIcone: ({ value, field }) => {
        const situacao = value === null || value === undefined ? '' : String(value);
        const cor = corDaSituacao(situacao, field);
        if (!cor) return null;
        return (
            <span className="grid-glifo-centro">
                <SituacaoGlifo situacao={situacao} cor={cor} title={field.filterList?.[situacao]} />
            </span>
        );
    },
    TarefaComIcone: ({ value, row, field }) => {
        if (value === null || value === undefined || value === '') return null;
        // O impedimento vence a situação da tarefa: é o que o supervisor precisa ver primeiro.
        const situacao = row?.travada ? 'travada' : String(row?.['tarefa/situacao'] ?? '');
        const cor = corDaSituacao(situacao, field);
        const abertas = Number(row?.abertas);
        const extras = Number.isFinite(abertas) ? abertas - 1 : 0;
        return (
            <span className="grid-tarefa">
                {cor && <SituacaoGlifo situacao={situacao} cor={cor} />}
                {String(value)}
                {row?.travada ? <CadeadoTravada /> : null}
                {extras > 0 && (
                    <span className="grid-tarefa-mais" title={`mais ${extras} tarefa(s) aberta(s) neste setor`}>
                        +{extras}
                    </span>
                )}
            </span>
        );
    },
    Dias: ({ value, row }) => {
        if (value === null || value === undefined || value === '') return null;
        const dias = Number(value);
        if (!Number.isFinite(dias)) return null;
        const tom = row?.travada ? ' grid-dias-travada' : row?.atrasada ? ' grid-dias-atrasada' : '';
        // "hoje" em vez de "0 d", como no protótipo: idade zero é a única que a
        // pessoa lê como informação e não como contagem.
        return <span className={`grid-dias${tom}`}>{dias === 0 ? 'hoje' : `${dias} d`}</span>;
    },
    Unidade: ({ value, field }) => {
        if (value === null || value === undefined || value === '') return '';
        const { decimals = 0, suffix = '' } = (field.renderParams ?? {}) as any;
        const formatted = Number(value).toLocaleString('pt-BR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        });
        return `${formatted}${suffix}`;
    },
    Dropdown: ({ value }) => {
        return <DropdownCell value={value} handlers={handlers} />;
    },
    AcaoOrcamentoV2: ({ value, row }) => {
        if (!value) return null;
        return (
            <a
                className="grid-cell-action-btn grid-cell-action-btn-primary"
                href={`${API_ENDPOINT}${value}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                Ver V2
            </a>
        );
    },
    AcaoNfse: ({ value, row }) => {
        if (row.nfse_numero) {
            const nfseVerUrl = `${API_ENDPOINT}${row.nfse_ver_link}`;
            return (
                <a className="grid-cell-action-btn grid-cell-action-btn-link" href={nfseVerUrl} target="_blank" rel="noopener noreferrer">
                    N&ordm; {row.nfse_numero}
                </a>
            );
        } else if (row.nfse_emitir_link) {
            const nfseEmitirUrl = `${API_ENDPOINT}${row.nfse_emitir_link}`;
            return (
                <a
                    className="grid-cell-action-btn grid-cell-action-btn-success"
                    href={nfseEmitirUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        if (!window.confirm('Emitir mesmo?')) {
                            e.preventDefault();
                        }
                    }}
                >
                    Emitir NFS-e
                </a>
            );
        }
        return null;
    },
});

export const CELL_RENDERERS = createCellRenderers();
